import * as amplitude from '@amplitude/analytics-browser';
import type { EnrichmentPlugin } from '@amplitude/analytics-browser/lib/esm/types';
import type { JSX, ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import useRisksmartUser from '../hooks/useRisksmartUser';
import { hasuraClaimsNamespace, hasuraDefaultRole } from '../rbac/jwt';
import { AmplitudeContext } from './AmplitudeContext';

interface AmplitudeProviderProps {
  apiKey: string;
  // When set, routes ingest through a first-party reverse proxy instead of
  // amplitude.com — defeats ad-blocker drops. Unset falls back to the SDK
  // default (direct to Amplitude).
  serverUrl?: string;
  children: ReactNode;
}

// Snapshot of the URL at the time of the last mousedown event.
// Used to override page URL on element click events, because in SPAs
// the click handler may trigger navigation (via React Router) before
// Amplitude's autocapture reads window.location for the event properties.
// mousedown fires before click in the DOM event sequence, so it captures
// the URL the user was actually on when they initiated the interaction.
let mousedownUrl: string | undefined;
let mousedownListenerRegistered = false;

const ELEMENT_INTERACTION_EVENTS = new Set([
  '[Amplitude] Element Clicked',
  '[Amplitude] Dead Click',
  '[Amplitude] Rage Click',
  '[Amplitude] Error Click',
]);

// Enrichment plugin — re-added after each amplitude.reset() call.
// Redacts page titles and fixes element click page URLs.
const enrichmentPlugin: EnrichmentPlugin = {
  name: 'enrichPageUrlPlugin',
  setup: async () => {
    if (mousedownListenerRegistered) {
      return;
    }
    mousedownListenerRegistered = true;
    document.addEventListener(
      'mousedown',
      () => {
        mousedownUrl = window.location.href;
      },
      { capture: true }
    );
  },
  execute: async (amplitudeEvent) => {
    if (amplitudeEvent.event_type === '$identify') {
      return amplitudeEvent;
    }

    if (amplitudeEvent.event_type === '$groupidentify') {
      return amplitudeEvent;
    }

    // Override page URL on element interaction events with the URL
    // captured at mousedown time (before navigation occurred).
    if (
      ELEMENT_INTERACTION_EVENTS.has(amplitudeEvent.event_type) &&
      mousedownUrl
    ) {
      const url = new URL(mousedownUrl);
      const safeUrl = `${url.origin}${url.pathname}`;
      amplitudeEvent.event_properties = {
        ...amplitudeEvent.event_properties,
        '[Amplitude] Page URL': safeUrl,
        '[Amplitude] Page Path': url.pathname,
        '[Amplitude] Page Location': safeUrl,
        '[Amplitude] Page Domain': url.hostname,
      };
      mousedownUrl = undefined;
    }

    amplitudeEvent.event_properties = {
      ...amplitudeEvent.event_properties,
      '[Amplitude] Page Title': 'Removed due to security',
    };

    return amplitudeEvent;
  },
};

export const AmplitudeProvider = (
  props: AmplitudeProviderProps
): JSX.Element => {
  const { isAuthenticated, user } = useRisksmartUser();
  const prevOrgKeyRef = useRef<string | undefined>(undefined);

  // Destructure all user-derived primitives used by effects for clearer dependencies.
  const orgKey = user?.orgKey;
  const userId = user?.userId;
  const email = user?.claims_email;
  const username = user?.claims_username;
  const orgRole = user?.[hasuraClaimsNamespace]?.[hasuraDefaultRole];
  const isCustomerSupport = user?.isCustomerSupport;
  const claimsOrgName = user?.claims_organization_name;
  const claimsTenant = user?.claims_tenant;
  const claimsOrgType = user?.claims_org_type;

  // Effect 1: Initialize Amplitude once, then set/update the org group when orgKey changes.
  useEffect(() => {
    // Skip init when no API key is configured (e.g. ephemeral/preview envs).
    // Initialising with an empty key makes the SDK 404 on its session-replay
    // config and reject every event with "missing API key", spamming the console.
    if (!isAuthenticated || !orgKey || !props.apiKey) {
      return;
    }

    if (prevOrgKeyRef.current === orgKey) {
      return; // orgKey unchanged, no action required.
    }

    prevOrgKeyRef.current = orgKey;

    amplitude.reset();

    amplitude.add(enrichmentPlugin);

    amplitude.init(props.apiKey, {
      serverUrl: props.serverUrl,
      autocapture: {
        attribution: false,
        // Page view plugin installed (for Page Counter enrichment in execute())
        // but trackOn: () => false prevents it from ever auto-firing.
        // All page views are fired manually from useBaseTracking.
        pageViews: { trackOn: () => false },
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: true,
        networkTracking: false,
      },
    });

    amplitude.setGroup('organization', orgKey);
    // serverUrl intentionally omitted: it is provided via props, but this
    // effect only re-initializes Amplitude when the authenticated org scope
    // changes. Including serverUrl here would suggest that runtime prop
    // changes are handled, but the prevOrgKeyRef guard short-circuits on
    // orgKey, so a serverUrl change would currently be ignored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.apiKey, isAuthenticated, orgKey]);

  // Effect 2: Identify user & group properties when any relevant user info changes (while authenticated & scoped to orgKey).
  useEffect(() => {
    // Mirror Effect 1's apiKey guard: with no key Amplitude is never init'd, so
    // identify/group calls would only queue against an SDK that never flushes.
    if (!isAuthenticated || !orgKey || !props.apiKey) {
      return;
    }

    // User identification
    if (userId) {
      void amplitude.setUserId(userId);
      const identifyEvent = new amplitude.Identify();
      if (email) {
        identifyEvent.set('email', email);
      }
      if (username) {
        identifyEvent.set('name', username);
      }
      if (orgRole) {
        identifyEvent.set('org_role', orgRole);
      }
      identifyEvent.set('isCustomerSupport', !!isCustomerSupport);
      amplitude.identify(identifyEvent);
    }

    // Group identification
    if (claimsOrgName || claimsTenant || claimsOrgType) {
      const groupIdentifyEvent = new amplitude.Identify();
      if (claimsOrgName) {
        groupIdentifyEvent.set('name', claimsOrgName);
      }
      if (claimsTenant) {
        groupIdentifyEvent.set('tenant', claimsTenant);
      }
      if (claimsOrgType) {
        groupIdentifyEvent.set('org_type', claimsOrgType);
      }
      amplitude.groupIdentify('organization', orgKey, groupIdentifyEvent);
    }
  }, [
    props.apiKey,
    isAuthenticated,
    orgKey,
    userId,
    email,
    username,
    orgRole,
    isCustomerSupport,
    claimsOrgName,
    claimsTenant,
    claimsOrgType,
  ]);

  return (
    <AmplitudeContext.Provider value={amplitude}>
      {props.children}
    </AmplitudeContext.Provider>
  );
};
